import { SelectInput } from "@/components/ui/select"
import { MailPlus } from "lucide-react"
import { useTranslation } from "react-i18next"
import { CreateParams, useNewProps } from "./page-props"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { FormField, FormGroup } from "@/components/ui/form-field"
import { ErrorMessage } from "@hookform/error-message"
import { ErrorHint } from "@/components/ui/errorhint"
import { SheetPageBody, SheetPageTitle } from "@/components/ui/sheet-page"
import * as PageRendering from "@/lib/page-rendering"

type InvitationFormValues = {
  invitation: {
    team_profile_id: number
    teacher_id: number
    state: "consideration" | "ready_to_accept"
  }
  ready_to_accept_message: string
  consideration_message: string
}

export default function Page() {
  const { t } = useTranslation(["common", "invitations"])

  const {
    team_profile_options,
    invitation: { teacher_id, teacher_name },
  } = useNewProps()
  const navigate = PageRendering.useNavigate()

  const { control, handleSubmit, register, formState } =
    useForm<InvitationFormValues>({
      defaultValues: {
        invitation: {
          teacher_id,
          team_profile_id:
            team_profile_options.length === 1
              ? team_profile_options[0].value
              : undefined,
        },
        ready_to_accept_message: t("invitations:new.ready_to_accept_pro_forma"),
        consideration_message: t("invitations:new.consideration_pro_forma"),
      },
    })

  const watchState = useWatch({ control, name: "invitation.state" })

  const submitInvitation: SubmitHandler<InvitationFormValues> = (data) => {
    const { invitation, consideration_message, ready_to_accept_message } = data

    let message: string | null = null
    switch (invitation.state) {
      case "consideration":
        message = consideration_message
        break
      case "ready_to_accept":
        message = ready_to_accept_message
        break
    }

    navigate({
      href: ".",
      method: "post",
      data: { invitation, message } satisfies CreateParams,
    })
  }

  return (
    <SheetPageBody>
      <SheetPageTitle icon={MailPlus}>
        {t("invitations:invitation.title", { teacher_name })}
      </SheetPageTitle>
      <p className="mb-6">{t("invitations:new.description")}</p>
      <form
        className="flex flex-col gap-6"
        data-testid="invitation-form"
        onSubmit={handleSubmit(submitInvitation)}
      >
        <input type="hidden" {...register("invitation.teacher_id")} />
        <FormGroup
          htmlFor="team-select"
          label={t("invitations:new.team_select_label")}
        >
          <Controller
            name="invitation.team_profile_id"
            control={control}
            rules={{ required: t("common:errors.required") }}
            render={({ field }) => (
              <SelectInput
                className="w-64"
                id="team-select"
                value={field.value}
                onChange={field.onChange}
                options={team_profile_options}
                valueAsNumber={true}
                placeholder={t("invitations:new.team_select_placeholder")}
              />
            )}
          />
          <ErrorMessage
            name="invitation.team_profile_id"
            errors={formState.errors}
            render={ErrorHint}
          />
        </FormGroup>

        <div className="space-y-2">
          <Label id="status-label" htmlFor="status-input">
            {t("invitations:new.initial_status_label")}
          </Label>
          <Controller
            control={control}
            name="invitation.state"
            rules={{ required: t("common:errors.required") }}
            render={({ field: { onChange } }) => (
              <RadioGroup
                required
                aria-labelledby="status-label"
                id="status-input"
                onValueChange={(value) => onChange(value)}
                className="items-top flex w-full flex-col space-y-2 space-x-2"
              >
                <div className="border-muted has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/10 has-data-[state=checked]:hover:bg-primary/15 has-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex flex-col gap-2 rounded-md border px-4 py-2 transition-colors">
                  <div className="flex flex-row items-center">
                    <RadioGroupItem
                      value="consideration"
                      id="pending-review"
                      className="peer"
                      aria-labelledby="pending-review-label"
                      aria-describedby="pending-review-description"
                    />
                    <Label
                      htmlFor="pending-review"
                      className="flex items-center gap-2 rounded-md px-4 py-2"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium" id="pending-review-label">
                          {t("invitations:new.initial_status.consideration")}
                        </span>
                        <span className="text-xs" id="private-description">
                          {t(
                            "invitations:new.initial_status.consideration_description",
                          )}
                        </span>
                      </div>
                    </Label>
                  </div>
                  <FormField
                    {...register("consideration_message")}
                    label={t("invitations:new.message_label")}
                  >
                    <Textarea disabled={watchState !== "consideration"} />
                  </FormField>
                </div>

                <div className="border-muted has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/10 has-data-[state=checked]:hover:bg-primary/15 has-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex flex-col gap-2 rounded-md border px-4 py-2 transition-colors">
                  <div className="flex flex-row items-center">
                    <RadioGroupItem
                      value="ready_to_accept"
                      id="ready-to-accept"
                      className="peer"
                      aria-labelledby="ready-to-accept-label"
                      aria-describedby="ready-to-accept-description"
                    />
                    <Label
                      htmlFor="ready-to-accept"
                      className="flex items-center gap-2 rounded-md px-4 py-2"
                    >
                      <div className="flex flex-col">
                        <span
                          className="font-medium"
                          id="ready-to-accept-label"
                        >
                          {t("invitations:new.initial_status.ready_to_accept")}
                        </span>
                        <span className="text-xs" id="private-description">
                          {t(
                            "invitations:new.initial_status.ready_to_accept_description",
                          )}
                        </span>
                      </div>
                    </Label>
                  </div>
                  <FormField
                    {...register("ready_to_accept_message")}
                    label={t("invitations:new.message_label")}
                  >
                    <Textarea disabled={watchState !== "ready_to_accept"} />
                  </FormField>
                </div>
              </RadioGroup>
            )}
          />
          <ErrorMessage
            name="invitation.state"
            errors={formState.errors}
            render={ErrorHint}
          />
        </div>

        <Button type="submit">Send Invitation</Button>
      </form>
    </SheetPageBody>
  )
}
